/**
 * Generate the evening post, then get out of the way.
 *
 * Editable before it is copied, because the person sending it always wants to
 * add a line — "unverified", "impact on standings coming shortly" — and a
 * generator that fights that just gets pasted into Notes first and edited there.
 */

import { useMemo, useState } from 'react'
import { matchNameIn } from '../engine/naming'
import { matchCounts } from '../engine/types'
import type { Tournament } from '../engine/types'
import { matchText, payoutText, rosterText, standingsText } from '../share'
import type { TournamentView } from '../select'
import { Bar, SectionRule } from '../ui'

export function Share({ doc, view }: { doc: Tournament; view: TournamentView }) {
  /*
   * Only rounds that count. What gets pasted into the group thread is a result
   * — "(6)" beside a man's name reads as six points towards the championship —
   * and a round played for fun has no points to report. Sharing it would put a
   * number in the thread that is on nobody's total.
   */
  const played = doc.matches.filter(
    (m) => matchCounts(m) && (view.byMatch[m.id]?.tabs ?? []).some((t) => t.thru > 0),
  )
  const [picked, setPicked] = useState<string[]>(() =>
    played.length ? [played[played.length - 1]!.id] : [],
  )
  const [withStandings, setWithStandings] = useState(true)
  const [withPayouts, setWithPayouts] = useState(false)
  const [withRoster, setWithRoster] = useState(false)
  const [note, setNote] = useState('')
  const [copied, setCopied] = useState(false)

  const generated = useMemo(() => {
    const blocks: string[] = []
    if (withStandings) blocks.push(standingsText(doc, view, note.trim() || undefined))
    // The roster goes first when it is asked for: it is the "who is playing"
    // message, sent before a week rather than during one.
    if (withRoster) blocks.unshift(rosterText(doc))
    for (const id of picked) {
      if (withPayouts) {
        const m = doc.matches.find((x) => x.id === id)
        const p = m ? payoutText(doc, m) : ''
        if (p) blocks.push(p)
      }
      const t = matchText(doc, view, id)
      if (t) blocks.push(t)
    }
    return blocks.join('\n\n')
  }, [doc, view, picked, withStandings, withPayouts, withRoster, note])

  const [text, setText] = useState<string | null>(null)
  const body = text ?? generated

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(body)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    } catch {
      // Clipboard is blocked in some in-app browsers; the textarea is right
      // there and selectable, so this is a nuisance rather than a dead end.
      setCopied(false)
    }
  }

  return (
    <>
      <Bar kicker={doc.name} title="Post it" sub="Standings and results, ready to paste" />

      <SectionRule>What goes in</SectionRule>
      <div className="px-4 py-2">
        <label className="flex items-center gap-2 py-1.5">
          <input
            type="checkbox"
            checked={withStandings}
            onChange={(e) => {
              setWithStandings(e.target.checked)
              setText(null)
            }}
            className="w-5 h-5 accent-[var(--color-ink)] bc-tap"
          />
          <span className="bc-name text-sm">Current standings</span>
        </label>
        <label className="flex items-center gap-2 py-1.5">
          <input
            type="checkbox"
            checked={withPayouts}
            onChange={(e) => {
              setWithPayouts(e.target.checked)
              setText(null)
            }}
            className="w-5 h-5 accent-[var(--color-ink)] bc-tap"
          />
          <span className="bc-name text-sm">Remind everyone what it pays</span>
        </label>
        <label className="flex items-center gap-2 py-1.5">
          <input
            type="checkbox"
            checked={withRoster}
            onChange={(e) => {
              setWithRoster(e.target.checked)
              setText(null)
            }}
            className="w-5 h-5 accent-[var(--color-ink)] bc-tap"
          />
          <span className="bc-name text-sm">Roster and indexes</span>
        </label>
      </div>

      <div className="px-4 pb-3">
        <label className="grid gap-1">
          <span className="bc-label text-mute">Standings note</span>
          <input
            value={note}
            onChange={(e) => {
              setNote(e.target.value)
              setText(null)
            }}
            placeholder="with 1 match left until the final"
            className="border-2 border-ink px-3 py-2 text-sm bc-tap"
          />
        </label>
      </div>

      <SectionRule>Which matches</SectionRule>
      <ul className="divide-y divide-rule">
        {played.map((m) => (
          <li key={m.id}>
            <button
              type="button"
              onClick={() => {
                setPicked((cur) =>
                  cur.includes(m.id) ? cur.filter((x) => x !== m.id) : [...cur, m.id],
                )
                setText(null)
              }}
              className={`w-full text-left px-4 py-2.5 bc-tap bc-name text-sm ${
                picked.includes(m.id) ? 'bg-ink text-paper' : ''
              }`}
            >
              {matchNameIn(doc, m)}
            </button>
          </li>
        ))}
        {played.length === 0 && (
          <li className="px-4 py-3 text-[13px] text-mute">Nothing has been scored yet.</li>
        )}
      </ul>

      <SectionRule>The post</SectionRule>
      <div className="px-4 py-3">
        <textarea
          value={body}
          onChange={(e) => setText(e.target.value)}
          rows={16}
          className="w-full border-2 border-ink p-3 text-xs font-mono leading-relaxed bc-tap"
        />
        <div className="flex gap-2 mt-2">
          <button
            type="button"
            onClick={copy}
            className="bc-tap flex-1 bg-signal text-paper bc-label py-3"
          >
            {copied ? 'Copied' : 'Copy'}
          </button>
          <button
            type="button"
            onClick={() => setText(null)}
            disabled={text === null}
            className="bc-tap border-2 border-ink bc-label px-4 disabled:opacity-30"
          >
            Reset
          </button>
        </div>
        <p className="text-[13px] text-mute leading-snug mt-2">
          Edit it freely before copying — the numbers are generated, the wording is
          yours. Quota shows the running total the scorecard uses, not the margin over
          quota, so it matches the card in your pocket.
        </p>
      </div>
    </>
  )
}
